import { useEffect, useState } from "react";
import { MessageSquare, Mail, Download, ChevronDown, ChevronUp, BarChart3 } from "lucide-react";
import { api } from "@/lib/api";
import { getAdminToken } from "@/lib/adminAuth";

const hdr = () => ({ headers: { Authorization: `Bearer ${getAdminToken()}` } });

const CAMP_LABEL = { welcome: "👋 Welcome", fund_nudge: "💰 Fund nudge", comeback: "🔥 Come-back" };

const pct = (n, d) => (d ? `${Math.round((n / d) * 100)}%` : "—");

const EmailStats = ({ stats, recipients }) => (
  <div data-testid="admin-email-stats" className="rounded-xl border border-white/10 bg-[#0B0E14] p-4 lg:col-span-2">
    <div className="flex items-center gap-2 mb-3">
      <BarChart3 className="w-4 h-4 text-[#F7B731]" />
      <h3 className="text-sm font-black text-white">Email performance</h3>
      <span className="ml-auto text-[9px] font-mono text-white/35">opens undercount (Apple Mail hides pixels) · clicks + came-back are exact</span>
    </div>
    <div className="grid gap-2 sm:grid-cols-3">
      {Object.entries(stats || {}).map(([c, s]) => (
        <div key={c} data-testid={`admin-email-stat-${c}`} className="rounded-lg border border-white/10 bg-[#131722] p-3">
          <p className="text-[11px] font-mono font-black text-white/85">{CAMP_LABEL[c] || c}</p>
          <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-[10px] font-mono">
            <span className="text-white/45">Sent</span><span className="text-right font-bold text-white">{s.sent}</span>
            <span className="text-white/45">Opened</span><span className="text-right font-bold text-[#53b9ff]">{s.opened} · {pct(s.opened, s.sent)}</span>
            <span className="text-white/45">Clicked</span><span className="text-right font-bold text-[#F7B731]">{s.clicked} · {pct(s.clicked, s.sent)}</span>
            <span className="text-white/45">Came back</span><span className="text-right font-bold text-[#089981]">{s.returned} · {pct(s.returned, s.sent)}</span>
            {"traded_after" in s && (<><span className="text-white/45">Traded after</span><span className="text-right font-bold text-[#089981]">{s.traded_after}</span></>)}
          </div>
        </div>
      ))}
    </div>
    <div className="mt-3 max-h-[260px] overflow-y-auto space-y-1">
      {(recipients || []).map((r) => (
        <div key={r.email} data-testid={`admin-email-recipient-${r.email}`} className="flex flex-wrap items-center gap-2 rounded-md border border-white/10 px-3 py-1.5 text-[10px] font-mono">
          <span className="font-bold text-white/80 truncate">{r.email}</span>
          {Object.entries(r.campaigns).map(([c, v]) => (
            <span key={c} className="rounded bg-white/5 px-1.5 py-0.5 text-[9px]">
              {CAMP_LABEL[c] || c}:{" "}
              <span className={v.clicked ? "text-[#F7B731]" : v.opened ? "text-[#53b9ff]" : "text-white/40"}>
                {v.clicked ? "clicked" : v.opened ? "opened" : "sent"}
              </span>
              {v.returned && <span className="text-[#089981]"> · back</span>}
              {v.traded_after && <span className="text-[#089981]"> · traded</span>}
            </span>
          ))}
        </div>
      ))}
      {recipients && !recipients.length && <p className="text-[10px] font-mono text-white/35">no lifecycle emails sent yet</p>}
    </div>
  </div>
);

const AdminInsights = () => {
  const [convos, setConvos] = useState(null);
  const [emails, setEmails] = useState(null);
  const [mailStats, setMailStats] = useState(null);
  const [openId, setOpenId] = useState(null);
  useEffect(() => {
    api.get("/admin/helpbot/conversations", hdr()).then(({ data }) => setConvos(data)).catch(() => setConvos({ conversations: [] }));
    api.get("/admin/marketing/emails", hdr()).then(({ data }) => setEmails(data)).catch(() => setEmails({ emails: [] }));
    api.get("/admin/marketing/stats", hdr()).then(({ data }) => setMailStats(data)).catch(() => setMailStats({ stats: null, recipients: [] }));
  }, []);
  const exportCsv = () => {
    const rows = ["email,source,account_id,created_at",
      ...(emails?.emails || []).map((r) => [r.email, r.source || "", r.account_id || "", r.created_at || ""].join(","))];
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([rows.join("\n")], { type: "text/csv" }));
    a.download = "vts_marketing_emails.csv";
    a.click();
  };
  return (
    <div className="grid gap-4 lg:grid-cols-2 mb-6">
      {/* 📊 lifecycle email funnel */}
      {mailStats?.stats && <EmailStats stats={mailStats.stats} recipients={mailStats.recipients} />}
      {/* 🤖 AI Coach conversations */}
      <div data-testid="admin-ai-conversations" className="rounded-xl border border-white/10 bg-[#0B0E14] p-4">
        <div className="flex items-center gap-2 mb-2">
          <MessageSquare className="w-4 h-4 text-[#53b9ff]" />
          <h3 className="text-sm font-black text-white">AI Coach conversations</h3>
          <span className="ml-auto text-[10px] font-mono text-white/40">{convos ? `${convos.sessions ?? (convos.conversations || []).length} sessions` : "loading…"}</span>
        </div>
        <div className="max-h-[420px] overflow-y-auto space-y-1.5">
          {(convos?.conversations || []).map((c) => (
            <div key={c.session_id} className="rounded-lg border border-white/10">
              <button data-testid={`admin-convo-${c.session_id}`} onClick={() => setOpenId(openId === c.session_id ? null : c.session_id)}
                className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-white/5">
                <span className="text-[10px] font-mono font-bold text-white/70 truncate flex-1">{c.session_id.slice(0, 18)}…</span>
                <span className="text-[9px] font-mono text-white/40">{c.messages.length} msgs · {String(c.last_at).slice(0, 16).replace("T", " ")}</span>
                {openId === c.session_id ? <ChevronUp className="w-3 h-3 text-white/40" /> : <ChevronDown className="w-3 h-3 text-white/40" />}
              </button>
              {openId === c.session_id && (
                <div className="px-3 pb-2 space-y-1.5">
                  {c.messages.map((m, i) => (
                    <div key={i} className="text-[10px] font-mono">
                      <p className="text-[#F7B731]">Q: {m.q}</p>
                      <p className="text-white/55">A: {m.a}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
          {convos && !(convos.conversations || []).length && <p className="text-[10px] font-mono text-white/35">no conversations yet</p>}
        </div>
      </div>
      {/* 📧 marketing email list */}
      <div data-testid="admin-marketing-emails" className="rounded-xl border border-white/10 bg-[#0B0E14] p-4">
        <div className="flex items-center gap-2 mb-2">
          <Mail className="w-4 h-4 text-[#089981]" />
          <h3 className="text-sm font-black text-white">Marketing emails</h3>
          <span className="text-[10px] font-mono text-white/40">{emails ? `${(emails.emails || []).length} subscribers` : "loading…"}</span>
          <button data-testid="admin-emails-export" onClick={exportCsv} disabled={!(emails?.emails || []).length}
            className="ml-auto flex items-center gap-1 rounded-md border border-[#089981]/50 px-2.5 py-1 text-[10px] font-mono font-black text-[#089981] hover:bg-[#089981]/10 disabled:opacity-30 transition-colors">
            <Download className="w-3 h-3" /> EXPORT CSV
          </button>
        </div>
        <p className="text-[9px] font-mono text-white/35 mb-2">load the CSV into Mailchimp / Brevo for campaigns — in-app bulk blasts are blocked by the email provider's anti-spam rules.</p>
        <div className="max-h-[380px] overflow-y-auto space-y-1">
          {(emails?.emails || []).map((r) => (
            <div key={r.email} className="flex items-center gap-2 rounded-md border border-white/10 px-3 py-1.5 text-[10px] font-mono">
              <span className="font-bold text-white/80 truncate flex-1">{r.email}</span>
              <span className="text-white/35">{r.source || ""}</span>
              <span className="text-white/35">{String(r.created_at || "").slice(0, 10)}</span>
            </div>
          ))}
          {emails && !(emails.emails || []).length && <p className="text-[10px] font-mono text-white/35">no subscribers yet</p>}
        </div>
      </div>
    </div>
  );
};

export default AdminInsights;
